/* eslint-disable no-unused-vars */
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import { Box, Skeleton } from '@mui/material';
import { FormikProvider, useFormik } from 'formik';
import { useFsConfirm } from '../../../../../shared/components/dialog';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	createSearchParams,
	useNavigate,
	useSearchParams,
} from 'react-router-dom';
import { IS_SA } from '../../../../../config/appConfig';
import { AppRoutes } from '../../../../../constants/appRoutes';
import { OTPTypes } from '../../../../../constants/otpTypes';
import { FormHelper } from '../../../../../helpers/formHelper';
import { buildLocalizeRoute } from '../../../../../helpers/languageHelper';
import { SystemSettingsService } from '../../../../../services/systemSettingsService';
import { TransferService } from '../../../../../services/transferService';
import { UserService } from '../../../../../services/userService';
import { InvestorService } from '../../../investorService';
import { SectionWithHeading } from '../../../../../shared/components/common/sectionWithHeading';
import { ButtonWrapper } from '../../../../../shared/components/formUtilities/useButton';
import { InputWrapper } from '../../../../../shared/components/formUtilities/useInput';
import { TextWrapper } from '../../../../../shared/components/formUtilities/useTextWrapper';
import { FSBankDetails } from '../../../../../shared/components/fsBankDetails';
import { OTPAuthenticationDialog } from '../../../../../shared/components/otpAuthenticationDialog';
import { transferSchema } from '../../../../../shared/validations/schema';
import { InvestorTransferHistory } from '../history';
import DepositSuccess from './depositSuccess';
import { getUser } from '../../../../../services/authService';
import { InvestorStatus } from '../../../../../constants/investorStatus';
import { OpenPanelCategories, OpenPanelEvents } from '../../../../../constants/openPanelEvents';
import { trackOpenPanelView } from '../../../../../helpers/openPanelHelper';
import { trackCustomerIoView } from '../../../../../helpers/customerIoHelper';

export const InvestorDeposit = () => {
	const systemSettingsService = new SystemSettingsService();
	const transferService = new TransferService();
	const userService = new UserService();
	const investorService = new InvestorService();
	const getTransactions = useRef(null);
	const [isLoadingDeposit, setIsLoadingDeposit] = useState(false);
	const [showReviewScreen, setShowReviewScreen] = useState(false);
	const [searchParams, setSearchParams] = useSearchParams();
	const [transferId, setTransferId] = useState(searchParams.get('tId'));
	const [userBankDetails, setUserBankDetails] = useState();
	const [showOTPAuth, setShowOTPAuth] = useState(false);
	const [loading, setLoading] = useState(true);
	const [isAllowShowPopup, setIsAllowShowPopup] = useState(false);
	const [userProfile, setUserProfile] = useState(null);
	const [loadingUserProfile, setLoadingUserProfile] = useState(true);
	const [virtualIban, setVirtualIban] = useState(null);
	const [loadingVirtualIban, setLoadingVirtualIban] = useState(IS_SA);
	const isWinddown = getUser()?.status === InvestorStatus.WindDown;

	const [isNotification, setIsNotification] = useState(
		searchParams.get('isNotificationTransaction')
	);

	const navigate = useNavigate();

	const { t } = useTranslation();
	const confirm = useFsConfirm();
	const form = useFormik({
		initialValues: { amount: searchParams.get('amount') || '' },
		validationSchema: transferSchema,
		onSubmit: () => {
			if (settings()?.enableDeposits) {
				onTransfer();
			} else {
				confirm({
					description: t('hasPausedAcceptingDeposits'),
					title: 'We Paused Deposits!',
					hideCancelButton: 'true',
					confirmationText: t('ok'),
				})
					.then(() => { })
					.catch(() => console.log('cancelled.'));
			}
		},
		...FormHelper.formikProps(),
	});

	const onTransfer = () => {
		if (isWinddown) {
			return;
		}

		setIsLoadingDeposit(true);
		transferService
			.addTransferIn(form.values.amount)
			.then((resp) => {
				setIsLoadingDeposit(false);
				if (resp.data?.otpRequested) {
					setTransferId(resp?.data?.correlationId || null);
					setTimeout(() => {
						setShowOTPAuth(true);
					});
				} else {
					showReceiptScreen(resp?.data?.correlationId);
				}
			})
			.catch((err) => {
				setIsLoadingDeposit(false);
			});
	};

	const settings = () => {
		return systemSettingsService.systemSettings();
	};

	// Check if user is Saudi national (nationality ID 1 or 2 represents Saudi Arabia)
	const isSaudiNational = () => {
		if (!userProfile?.nationality?.id) return false;
		return userProfile.nationality.id === 1 || userProfile.nationality.id === 2;
	};

	// Check if user is Saudi resident (address country ID 1 or 2 represents Saudi Arabia)
	const isSaudiResident = () => {
		if (!userProfile?.address?.countryId) return false;
		return userProfile.address.countryId === 1 || userProfile.address.countryId === 2;
	};

	// Determine if deposit notifications should be shown
	const shouldShowDepositNotification = () => {
		if (!IS_SA) return true; // Show for all users on non-SA platform
		// While loading, don't show notification to prevent flashing
		if (loadingUserProfile || loadingVirtualIban) return false;
		// On SA platform, hide for Saudi nationals, Saudi residents, or investors with a virtual IBAN
		return !isSaudiNational() && !isSaudiResident() && !virtualIban;
	};

	const getUserProfile = () => {
		setLoadingUserProfile(true);
		investorService
			.getInvestor()
			.then((resp) => {
				setUserProfile(resp.data);
				setLoadingUserProfile(false);
			})
			.catch((err) => {
				console.error('Failed to fetch user profile:', err);
				setLoadingUserProfile(false);
			});
	};

	const fetchVirtualIban = () => {
		setLoadingVirtualIban(true);
		userService.getVirtualIban()
			.then((resp) => {
				setVirtualIban(resp.data);
			})
			.finally(() => {
				setLoadingVirtualIban(false);
			});
	};

	useEffect(() => {
		trackOpenPanelView(OpenPanelEvents.depositRequestViewed, {
			page_name: 'Deposit',
			category: OpenPanelCategories.checkout,
		});
		trackCustomerIoView(OpenPanelEvents.depositRequestViewed, {
			page_name: 'Deposit',
			category: OpenPanelCategories.checkout,
		});
		setIsNotification(searchParams.get('isNotificationTransaction'));
		setTransferId(searchParams.get('tId'));
		getUserBankDetails();
		getUserProfile();
		if (IS_SA) {
			fetchVirtualIban();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (searchParams.get('amount') && searchParams.get('tId')) {
			setShowReviewScreen(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchParams.get('amount'), searchParams.get('tId')]);

	useEffect(() => {
		if (isNotification === 'true') {
			setTimeout(() => {
				document
					.getElementById('isNotification')
					.scrollIntoView({ behavior: 'smooth' });
			}, [600]);
		}
	}, [isNotification]);

	const getUserBankDetails = () => {
		setLoading(true);
		investorService
			.getBankDetails()
			.then((resp) => {
				setUserBankDetails(resp.data);
				setLoading(false);
			})
			.catch((err) => {
				setLoading(false);
			});
	};

	const showReceiptScreen = (transferId = null) => {
		if (transferId) {
			setTransferId(transferId);
		}
		setShowReviewScreen(true);
		getTransactions?.current();
		navigate(
			{
				pathname: buildLocalizeRoute(
					`${AppRoutes.Investor}/${AppRoutes.ITransfer}/${AppRoutes.IDeposit}`
				),
				search: createSearchParams({
					tId: transferId,
					amount: form.values.amount,
				}).toString(),
			},
			{ relative: `path` }
		);
	};

	const onOTPClose = (value) => {
		setShowOTPAuth(false);
		if (value) {
			showReceiptScreen();
		}
	};

	const onDoneTransaction = () => {
		navigate(
			buildLocalizeRoute(`${AppRoutes.Investor}/${AppRoutes.Marketplace}`)
		);
	};

	const onSetPopupValue = (transactions = []) => {
		let hasDeposit = true;
		if (transactions && transactions?.length) {
			hasDeposit = !!transactions.find(resp => resp.isDeposit);
			if (!hasDeposit) {
				setIsAllowShowPopup(true);
			}
		} else if (getUser()?.isApproved && !transactions?.length) {
			setIsAllowShowPopup(true);
		} else {
			setIsAllowShowPopup(false);
		}
	};

	const getLoader = () => {
		return (
			<Skeleton
				variant="text"
				sx={{ fontSize: '10px', width: '100px' }}
			/>
		);
	};

	return (
		<Box className="flex flex-col space-y-4">
			<Box className="grid md:grid-cols-2 grid-cols-1 gap-4">
				<SectionWithHeading
					classList={`!h-auto ${shouldShowDepositNotification() ? '!order-1' : '!order-2'}`}
					heading={
						<Box className="flex items-center">
							<span className="mx-2">
								{shouldShowDepositNotification() ? <span>
									{!showReviewScreen
										? t('depositNotification')
										: t('depositRequestReceived')}
								</span> : t('importantInfo')}
							</span>
						</Box>
					}
					children={
						!showReviewScreen ? (
							<Box className="grid grid-flow-row gap-6">
								{shouldShowDepositNotification() ? <Box>
									<TextWrapper
										classList="caption !text-sm"
										text={t('pleaseEnterTransferAmount')}></TextWrapper>
									<FormikProvider value={form}>
										<Box className="mt-3 flex flex-wrap flex-0 md:w-[440px]">
											<fieldset disabled={isWinddown}>
												<InputWrapper
													inputClass='currency-symbol'
													autoFocus
													isCurrencyText
													label={`${t('amount')}`}
													key={'amount'}
													name="amount"></InputWrapper>
											</fieldset>
											<ButtonWrapper
												onClick={() => {
													form.submitForm();
												}}
												disabled={isWinddown}
												loading={isLoadingDeposit}
												icon={
													<NotificationsNoneOutlinedIcon></NotificationsNoneOutlinedIcon>
												}
												classList="!min-w-[130px] !mt-[22px] !mx-3 !whitespace-nowrap"
												label={t('getTransferInstruction')}></ButtonWrapper>
										</Box>
									</FormikProvider>
								</Box> : null}
								<Box className="flex flex-col flex-0 justify-between h-full">
									{shouldShowDepositNotification() ? <TextWrapper
										classList="!text-lg !font-[700]"
										text={t('importantInfo')}></TextWrapper> : null}
									<Box className={`grid grid-flow-row gap-3 ${shouldShowDepositNotification() ? 'mt-3' : ''}`}>
										<Box>
											<TextWrapper
												classList="caption !text-sm"
												text={t(
													'depositMoneyIntoFundinSouqAccountText'
												)}></TextWrapper>
										</Box>
										<Box className="grid grid-flow-row gap-3">
											<Box className="flex flex-0 items-start">
												<CheckCircleOutlineOutlinedIcon
													className="!text-[20px]"
													color="primary"></CheckCircleOutlineOutlinedIcon>
												<TextWrapper
													classList="caption !text-sm !mx-1"
													text={t(
														'depositMoneyIntoFundinSouqAccountText1'
													)}></TextWrapper>
											</Box>
											<Box className="flex flex-0 items-start">
												<CheckCircleOutlineOutlinedIcon
													className="!text-[20px]"
													color="primary"></CheckCircleOutlineOutlinedIcon>
												<TextWrapper
													classList="caption !text-sm !mx-1"
													text={t(
														'depositMoneyIntoFundinSouqAccountText2'
													)}></TextWrapper>
											</Box>
											<Box className="flex flex-0 items-start">
												<CheckCircleOutlineOutlinedIcon
													className="!text-[20px]"
													color="primary"></CheckCircleOutlineOutlinedIcon>
												<TextWrapper
													classList="caption !text-sm !mx-1"
													text={
														!shouldShowDepositNotification()
															? t('yourFundsAreSecurelyANB')
															: t('yourFundsAreSecurely')
													}></TextWrapper>
											</Box>
										</Box>
									</Box>
								</Box>
							</Box>
						) : (
							<DepositSuccess
								isAllowShowPopup={isAllowShowPopup}
								onDoneTransaction={() => {
									onDoneTransaction();
								}}
								onCancel={() => {
									getTransactions?.current();
									setTransferId(null);
									setShowReviewScreen(false);
									navigate(
										{
											pathname: buildLocalizeRoute(
												`${AppRoutes.Investor}/${AppRoutes.ITransfer}/${AppRoutes.IDeposit}`
											),
										},
										{ relative: `path` }
									);
								}}
								amount={form.values.amount}
								transferId={transferId}
								userBankDetails={userBankDetails}></DepositSuccess>
						)
					}></SectionWithHeading>
				<SectionWithHeading
					classList={`!h-auto ${shouldShowDepositNotification() ? '!order-2' : '!order-1'}`}
					bodyClass="!h-full"
					heading={
						<Box className="flex items-center">
							<span className="mx-2">{t('depositInfo')}</span>
						</Box>
					}
					children={
						<Box className={`flex ${shouldShowDepositNotification() ? 'flex-col space-y-4' : 'flex-col-reverse'}`}>
							{shouldShowDepositNotification() ? <Box className={`grid grid-flow-row gap-3 ${!shouldShowDepositNotification() ? 'mt-4' : ''}`}>
								<TextWrapper
									classList="!text-lg !font-[700]"
									text={t('yourDepositReference')}></TextWrapper>
								<Box className="grid grid-cols-3  items-center">
									<TextWrapper
										classList="!caption !text-sm"
										text={t('reference')}></TextWrapper>
									<TextWrapper
										classList="!font-bold !text-sm md:!col-span-2 !mx-2"
										text={
											!loading ? userBankDetails?.reference : getLoader()
										}></TextWrapper>
								</Box>
								<Box className="grid grid-cols-3 items-center">
									<TextWrapper
										classList="!caption !text-sm"
										text={t('bankName')}></TextWrapper>
									<TextWrapper
										classList="!font-bold !text-sm md:!col-span-2 !mx-2"
										text={
											!loading ? userBankDetails?.bankName : getLoader()
										}></TextWrapper>
								</Box>
								<Box className="grid grid-cols-3 items-center">
									<TextWrapper
										classList="!caption !text-sm"
										text={t('accountName')}></TextWrapper>
									<TextWrapper
										classList="!font-bold !text-sm md:!col-span-2 !mx-2"
										text={
											!loading ? userBankDetails?.accountName : getLoader()
										}></TextWrapper>
								</Box>
							</Box> : null}
							<FSBankDetails></FSBankDetails>
							{shouldShowDepositNotification() ? <small className='caption !text-[10px]'>{t('routingNumberInfo')}</small> : null}
						</Box>
					}></SectionWithHeading>
			</Box>
			<Box id="isNotification">
				<InvestorTransferHistory
					onInitTransaction={(transactions) => {
						onSetPopupValue(transactions);
					}}
					getTransactions={getTransactions}
				// type={TransactionTypes.Deposit}
				/>
			</Box>
			{showOTPAuth ? (
				<OTPAuthenticationDialog
					type={OTPTypes.Transaction}
					state={showOTPAuth}
					corrolationId={transferId}
					onClose={(e) => {
						onOTPClose(e);
					}}></OTPAuthenticationDialog>
			) : null}
		</Box>
	);
};
